import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import {
  Activity,
  ActivityParticipant,
} from '../interfaces/activity.interfaces';
import { environment } from '../../environments/environment';

interface BackendActivity {
  _id: string;
  title: string;
  description?: string;
  type: string;
  valueInHours: number;
  startDate?: string | Date;
  dueDate?: string | Date;
  startTime?: string;
  endTime?: string;
  url?: string;
  maxParticipants?: number | null;
  participantCount?: number;
  created?: string | Date;
  updated?: string | Date;
}

interface ApiListResponse {
  status: string;
  results: number;
  data: { documents: BackendActivity[] };
}

interface ApiDocumentResponse {
  status: string;
  data: { document: BackendActivity };
}

interface ApiParticipantsResponse {
  status: string;
  results: number;
  data: {
    documents: ActivityParticipant[];
  };
}

function toDate(value: string | Date | undefined): Date {
  if (!value) return new Date();
  return value instanceof Date ? value : new Date(value);
}

function mapActivity(doc: BackendActivity): Activity {
  return {
    id: doc._id,
    idActividad: doc._id,
    title: doc.title,
    description: doc.description ?? '',
    type: doc.type,
    valueInHours: doc.valueInHours,
    startDate: toDate(doc.startDate),
    dueDate: toDate(doc.dueDate),
    startTime: doc.startTime ?? '',
    endTime: doc.endTime ?? '',
    url: doc.url ?? '',
    maxParticipants: doc.maxParticipants ?? null,
    participantCount: doc.participantCount ?? 0,
    created: toDate(doc.created),
    updated: toDate(doc.updated),
  };
}

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/activity`;

  newActivity(activity: Partial<Activity>): Promise<Activity> {
    return this.http
      .post<ApiDocumentResponse>(`${this.baseUrl}/`, activity)
      .pipe(map((res) => mapActivity(res.data.document as BackendActivity)))
      .toPromise()
      .then((r) => r as Activity);
  }

  getAllActivities(): Observable<Activity[]> {
    return this.http.get<ApiListResponse>(`${this.baseUrl}/`).pipe(
      tap((res) => {
        console.log('🔥 ACTIVIDADES CRUDAS DESDE ACTIVITY SERVICE:', res.data.documents);

        console.log(
          '🔥 CUPOS CRUDOS:',
          res.data.documents.map((activity) => ({
            title: activity.title,
            maxParticipants: activity.maxParticipants,
            participantCount: (activity as BackendActivity & {
              participantCount?: number;
            }).participantCount,
          })),
        );
      }),

      map((res) => res.data.documents.map(mapActivity)),

      tap((activities) => {
        console.log('🔥 ACTIVIDADES DESPUÉS DE MAPEAR:', activities);

        console.log(
          '🔥 CUPOS DESPUÉS DEL MAPPER:',
          activities.map((activity) => ({
            title: activity.title,
            maxParticipants: activity.maxParticipants,
            participantCount: activity.participantCount,
          })),
        );
      }),
    );
  }

  getObligatoryActivities(): Observable<Activity[]> {
    return this.getAllActivities().pipe(
      map((activities) => activities.filter((a) => a.type === 'Obligatorio')),
    );
  }

  async getActivityById(id: string): Promise<Activity> {
    const res = await this.http
      .get<ApiDocumentResponse>(`${this.baseUrl}/${id}`)
      .toPromise();
    return mapActivity(res!.data.document as BackendActivity);
  }

  getActivityParticipants(activityId: string): Observable<ActivityParticipant[]> {
    return this.http
      .get<ApiParticipantsResponse>(
        `${this.baseUrl}/${activityId}/participants`,
      )
      .pipe(map((res) => res.data.documents));
  }

  updateActivity(id: string, activity: Activity): void {
    this.http.patch(`${this.baseUrl}/${id}`, activity).subscribe();
  }

  deleteActivity(id: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }

  async registerActivity(id: string): Promise<void> {
    await this.http.post(`${this.baseUrl}/${id}/assign`, {}).toPromise();
  }

  cancelRegistration(
    activityId: string,
  ): Observable<unknown> {
    return this.http.patch(
      `${this.baseUrl}/${activityId}/cancel-registration`,
      {},
    );
  }

  updateParticipationStatus(
    activityId: string,
    studentId: string,
    status: 'registered' | 'attended' | 'absent' | 'validated',
    validatedHours?: number,
    performance?: string,
    performanceComment?: string,
  ): Observable<unknown> {
    return this.http.patch(
      `${this.baseUrl}/${activityId}/participants/${studentId}`,
      {
        status,
        ...(validatedHours !== undefined && {
          validatedHours,
        }),
        ...(performance !== undefined && {
          performance,
        }),
        ...(performanceComment !== undefined && {
          performanceComment,
        }),
      },
    );
  }
}
